
import React, { useState, useEffect } from 'react';
import { Project, WbsItem, LogEntry, AppTheme } from './types';
import { PersistenceManager } from './services/db';
import { auth, IS_MOCK_MODE } from './services/firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import JobBook from './components/JobBook';
import ProjectOverview from './components/ProjectOverview';
import LogsPage from './components/LogsPage';
import Chatbot from './components/Chatbot';
import AuthGate from './components/AuthGate';

type ViewState = 'JOB_BOOK' | 'PROJECT_OVERVIEW' | 'LOGS';

const App: React.FC = () => {
  const [view, setView] = useState<ViewState>('JOB_BOOK');
  const [theme, setTheme] = useState<AppTheme>(AppTheme.ZEN);
  const [user, setUser] = useState<User | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [wbs, setWbs] = useState<WbsItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Initialization & Auth Observer
  useEffect(() => {
    if (IS_MOCK_MODE) {
      // Bypass auth in mock mode for instant loading
      initAppData();
      return;
    }

    if (!auth) {
      console.warn("MarginGuard: Auth service Not Initialized (IS_MOCK_MODE is", IS_MOCK_MODE, ")");
      setLoading(false);
      if (IS_MOCK_MODE) initAppData();
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (!u) {
        setLoading(false);
      } else {
        initAppData();
      }
    });
    return () => unsubscribe();
  }, []);

  const initAppData = async () => {
    try {
      setLoading(true);
      const p = await PersistenceManager.getProjects();
      setProjects(p);
    } catch (err) {
      console.error("Forensic Retrieval Failed", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedProject) {
      const loadProjectData = async () => {
        try {
          const [l, w] = await Promise.all([
            PersistenceManager.getProjectLogs(selectedProject.id),
            PersistenceManager.getProjectWbs(selectedProject.id)
          ]);
          setLogs(l);
          setWbs(w);
        } catch (err) {
          console.error("Project Sync Error", err);
        }
      };
      loadProjectData();
    }
  }, [selectedProject]);

  const handleSelectProject = (project: Project) => {
    setSelectedProject(project);
    setView('PROJECT_OVERVIEW');
  };

  const getThemeStyles = () => {
    switch (theme) {
      case AppTheme.BAMBOO:
        return {
          bg: "bg-[#fefae0]",
          text: "text-[#283618]",
          sidebar: "bg-white border-[#ccd5ae]",
          sidebarActive: "bg-[#faedcd] text-[#283618]",
          accent: "text-[#606c38]",
          primaryBtn: "bg-[#283618] text-[#fefae0]",
        };
      case AppTheme.OCEAN:
        return {
          bg: "bg-[#f1faff]",
          text: "text-[#003049]",
          sidebar: "bg-white border-[#d1e9ff]",
          sidebarActive: "bg-[#e2f3ff] text-[#003049]",
          accent: "text-[#669bbc]",
          primaryBtn: "bg-[#003049] text-white",
        };
      default: // ZEN
        return {
          bg: "bg-[#f2f2f2]",
          text: "text-black",
          sidebar: "bg-white border-slate-100",
          sidebarActive: "bg-[#f2f2f2] text-black",
          accent: "text-slate-400",
          primaryBtn: "bg-black text-white",
        };
    }
  };

  const styles = getThemeStyles();

  if (loading) return (
    <div className={`h-screen w-screen flex items-center justify-center ${styles.bg}`}>
      <div className="flex flex-col items-center gap-4">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-black rounded-full animate-spin"></div>
        <p className="text-[10px] font-black uppercase tracking-[0.3em] opacity-30">Decrypting Forensic Node</p>
      </div>
    </div>
  );

  // In Mock Mode, we bypass the AuthGate
  if (!user && !IS_MOCK_MODE) return <AuthGate onSuccess={() => { }} />;

  return (
    <div className={`flex h-screen overflow-hidden font-sans antialiased ${styles.text} ${styles.bg} transition-colors duration-700`}>
      <aside className={`w-64 ${styles.sidebar} flex flex-col py-8 border-r shrink-0 transition-colors duration-700`}>
        <div className="px-8 mb-12 flex items-center gap-3">
          <div className={`w-8 h-8 ${styles.primaryBtn} rounded-lg flex items-center justify-center font-black text-xs shadow-sm`}>MG</div>
          <span className="font-bold tracking-tight text-sm">MarginGuard</span>
        </div>

        <nav className="flex-1 px-4 space-y-1">
          <button
            onClick={() => setView('JOB_BOOK')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-200 text-xs font-bold uppercase tracking-widest ${view === 'JOB_BOOK' ? styles.sidebarActive + ' shadow-inner' : styles.accent + ' hover:text-black hover:bg-slate-50'}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
            Job Book
          </button>

          <button
            disabled={!selectedProject}
            onClick={() => setView('PROJECT_OVERVIEW')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-200 text-xs font-bold uppercase tracking-widest disabled:opacity-20 ${view === 'PROJECT_OVERVIEW' ? styles.sidebarActive + ' shadow-inner' : styles.accent + ' hover:text-black hover:bg-slate-50'}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
            Profit Radar
          </button>

          <button
            disabled={!selectedProject}
            onClick={() => setView('LOGS')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-200 text-xs font-bold uppercase tracking-widest disabled:opacity-20 ${view === 'LOGS' ? styles.sidebarActive + ' shadow-inner' : styles.accent + ' hover:text-black hover:bg-slate-50'}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
            Audit Bridge
          </button>

          <div className="pt-12 pb-4 px-4 text-[9px] font-black text-slate-300 uppercase tracking-[0.2em]">Atmosphere</div>
          <div className="px-4 space-y-2">
            {[AppTheme.ZEN, AppTheme.BAMBOO, AppTheme.OCEAN].map((t) => (
              <button
                key={t}
                onClick={() => setTheme(t)}
                className={`w-full text-left px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${theme === t ? styles.sidebarActive : styles.accent + ' hover:bg-slate-50'}`}
              >
                {t}
              </button>
            ))}
          </div>

          {!IS_MOCK_MODE && (
            <button
              onClick={() => auth.signOut()}
              className="w-full mt-12 flex items-center gap-3 px-8 py-3 text-[9px] font-black uppercase tracking-widest text-rose-400 hover:text-rose-600 transition-all"
            >
              Logout Node
            </button>
          )}
        </nav>

        <div className="mt-auto px-8 py-8 border-t border-slate-50">
          <div className="flex items-center gap-3">
            <div className={`w-2 h-2 rounded-full ${IS_MOCK_MODE ? 'bg-amber-400' : 'bg-emerald-400'} shadow-sm animate-pulse`}></div>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              {IS_MOCK_MODE ? "MOCK MODE (OFFLINE)" : "Guam HQ Active"}
            </span>
          </div>
        </div>
      </aside>

      <main className="flex-1 relative overflow-hidden flex flex-col">
        <div className="flex-1 overflow-y-auto">
          {view === 'JOB_BOOK' && (
            <JobBook projects={projects} theme={theme} onSelectProject={handleSelectProject} />
          )}

          {view === 'PROJECT_OVERVIEW' && selectedProject && (
            <ProjectOverview theme={theme} project={selectedProject} onNavigateToLogs={() => setView('LOGS')} />
          )}

          {view === 'LOGS' && selectedProject && (
            <LogsPage
              theme={theme}
              project={selectedProject}
              wbs={wbs}
              logs={logs}
              setLogs={setLogs}
              onBack={() => setView('PROJECT_OVERVIEW')}
            />
          )}
        </div>

        {selectedProject && (
          <Chatbot theme={theme} project={selectedProject} logs={logs} />
        )}
      </main>
    </div>
  );
};

export default App;
